import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Building, Network } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const TriangleSection = () => {
  const { t } = useLanguage();

  const benefits = [
    { icon: Globe, key: 'benefit1' },
    { icon: Building, key: 'benefit2' },
    { icon: Network, key: 'benefit3' },
  ];

  return (
    <section
      id="triangle"
      data-testid="triangle-section"
      className="py-24 md:py-32 bg-[#1A1F4B] relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute top-0 left-0 w-full h-full"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9A84C' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mb-4">
            {t('triangle.title')}
          </h2>
          <p className="text-[#C9A84C] text-lg md:text-xl font-medium">
            {t('triangle.tagline')}
          </p>
        </div>

        {/*
          viewBox 0 0 800 460
          Vertices: Brasil=(400,50)  EUA=(100,350)  Ásia=(700,350)
          Circles r=36 (diameter 72):
            Brasil  foreignObject x=364 y=14   → centre (400, 50)
            EUA     foreignObject x=64  y=314  → centre (100,350)
            Ásia    foreignObject x=664 y=314  → centre (700,350)
        */}
        <div className="relative mb-20 max-w-4xl mx-auto">
          <svg
            viewBox="0 0 800 460"
            className="w-full h-auto"
            data-testid="triangle-svg"
            overflow="visible"
          >
            <defs>
              <linearGradient id="triGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#C9A84C" />
                <stop offset="100%" stopColor="#1A1F4B" />
              </linearGradient>
            </defs>

            <path d="M400,50 L100,350 L700,350 Z" fill="url(#triGrad)" opacity="0.1" />

            {/* Draw animation — fires once, lightweight */}
            <motion.path
              d="M400,50 L100,350 L700,350 Z"
              fill="none"
              stroke="#C9A84C"
              strokeWidth="2"
              strokeDasharray="10,5"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 2 }}
            />

            {/* Brasil node — (400, 50) */}
            <g data-testid="node-brazil">
              <circle cx="400" cy="50" r="36" fill="#C9A84C" />
              <foreignObject x="364" y="14" width="72" height="72">
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  style={{ width: '72px', height: '72px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '30px', lineHeight: 1 }}
                >🇧🇷</div>
              </foreignObject>
              <text x="400" y="106" textAnchor="middle" fill="white" fontSize="15" fontWeight="bold" fontFamily="Manrope, sans-serif">{t('triangle.brazil')}</text>
              <text x="400" y="123" textAnchor="middle" fill="#C9A84C" fontSize="12" fontFamily="Manrope, sans-serif">{t('triangle.brazilCity')}</text>
            </g>

            {/* EUA node — (100, 350) */}
            <g data-testid="node-usa">
              <circle cx="100" cy="350" r="36" fill="#C9A84C" />
              <foreignObject x="64" y="314" width="72" height="72">
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  style={{ width: '72px', height: '72px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '30px', lineHeight: 1 }}
                >🇺🇸</div>
              </foreignObject>
              <text x="100" y="406" textAnchor="middle" fill="white" fontSize="15" fontWeight="bold" fontFamily="Manrope, sans-serif">{t('triangle.usa')}</text>
              <text x="100" y="423" textAnchor="middle" fill="#C9A84C" fontSize="12" fontFamily="Manrope, sans-serif">{t('triangle.usaCity')}</text>
            </g>

            {/* Ásia node — (700, 350) */}
            <g data-testid="node-asia">
              <circle cx="700" cy="350" r="36" fill="#C9A84C" />
              <foreignObject x="664" y="314" width="72" height="72">
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  style={{ width: '72px', height: '72px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '30px', lineHeight: 1 }}
                >🌏</div>
              </foreignObject>
              <text x="700" y="406" textAnchor="middle" fill="white" fontSize="15" fontWeight="bold" fontFamily="Manrope, sans-serif">{t('triangle.asia')}</text>
              <text x="700" y="423" textAnchor="middle" fill="#C9A84C" fontSize="12" fontFamily="Manrope, sans-serif">{t('triangle.asiaCount')}</text>
            </g>
          </svg>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {benefits.map((benefit) => {
            const Icon = benefit.icon;
            return (
              <div
                key={benefit.key}
                className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 text-center hover:border-[#C9A84C]/50 transition-colors"
                data-testid={`benefit-${benefit.key}`}
              >
                <div className="w-14 h-14 bg-[#C9A84C]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-7 h-7 text-[#C9A84C]" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">
                  {t(`triangle.${benefit.key}`)}
                </h3>
                <p className="text-white/60 text-sm">
                  {t(`triangle.${benefit.key}Desc`)}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TriangleSection;
